import { computed, nextTick } from "vue";
import type { Ref } from "vue";
import shopStore from "@/store/shopStore";

/**
 * 確認ダイアログ表示判定・整形に必要な依存をまとめた型。
 */
interface UseDeliveryRequestRegisterConfirmParams {
  formRef: Ref<any>;
  displayConfirmDialog: Ref<boolean>;
  deliveryRequestCreateInput: any;
  commaPyamentAmount: Ref<string>;
  dummy_payment_amount: Ref<string>;
  memberNotesBefore: Ref<string>;
  deliveryNotesBefore: Ref<string>;
  messageMemberNotes: Ref<string>;
  messageDeliveryNotes: Ref<string>;
  isDoubleDeli: Ref<boolean>;
  isCharterDeli: Ref<boolean>;
  doubleDeliOriconCount: Ref<number | null | undefined>;
  charterDeliOriconCount: Ref<number | null | undefined>;
  paymentRequiredFlg: Ref<boolean>;
  house_card_required: Ref<number>;
  wordList: Ref<Record<string, string>>;
  displayDeliveryTime: Ref<string>;
  isWordListReady: Ref<boolean>;
}

/**
 * 入力チェック後の確認表示と、確認画面向けの表示値作成を行う composable。
 */
export const useDeliveryRequestRegisterConfirm = ({
  formRef,
  displayConfirmDialog,
  deliveryRequestCreateInput,
  commaPyamentAmount,
  dummy_payment_amount,
  memberNotesBefore,
  deliveryNotesBefore,
  messageMemberNotes,
  messageDeliveryNotes,
  isDoubleDeli,
  isCharterDeli,
  doubleDeliOriconCount,
  charterDeliOriconCount,
  paymentRequiredFlg,
  house_card_required,
  wordList,
  displayDeliveryTime,
  isWordListReady,
}: UseDeliveryRequestRegisterConfirmParams) => {
  const onClickOpenConfirmDialog = async (): Promise<void> => {
    if (formRef.value) {
      const result = await formRef.value.validate();
      if (!result.valid) {
        return;
      }
    }
    displayConfirmDialog.value = true;
    nextTick(() => {
      window.scrollTo(window.pageXOffset, 50);
    });

    const numberFormat = (num: number): string => {
      return new Intl.NumberFormat().format(num);
    };
    if (deliveryRequestCreateInput.payment_amount! > 0) {
      commaPyamentAmount.value = numberFormat(
        deliveryRequestCreateInput.payment_amount!
      );
    }
    if (deliveryRequestCreateInput.member_notes?.includes('"')) {
      memberNotesBefore.value = deliveryRequestCreateInput.member_notes;
      deliveryRequestCreateInput.member_notes =
        deliveryRequestCreateInput.member_notes.replace(/"/g, "");
      messageMemberNotes.value =
        "(※会員備考のダブルクォーテーションは削除されています)";
    }
    if (deliveryRequestCreateInput.delivery_notes?.includes('"')) {
      deliveryNotesBefore.value = deliveryRequestCreateInput.delivery_notes;
      deliveryRequestCreateInput.delivery_notes =
        deliveryRequestCreateInput.delivery_notes.replace(/"/g, "");
      messageDeliveryNotes.value =
        "(※配達備考のダブルクォーテーションは削除されています)";
    }

    isDoubleDeli.value = false;
    isCharterDeli.value = false;
    doubleDeliOriconCount.value = shopStore.getDoubleDeliOriconCount;
    charterDeliOriconCount.value = shopStore.getCharterDeliOriconCount;
    if (
      doubleDeliOriconCount.value !== null &&
      deliveryRequestCreateInput.unit >= doubleDeliOriconCount.value! &&
      deliveryRequestCreateInput.unit < charterDeliOriconCount.value!
    ) {
      isDoubleDeli.value = true;
    } else if (
      charterDeliOriconCount.value !== null &&
      deliveryRequestCreateInput.unit >= charterDeliOriconCount.value!
    ) {
      isCharterDeli.value = true;
    }
  };

  const hasValidationErrors = computed(() => {
    const errors = formRef.value?.errors?.value;
    if (!errors) {
      return false;
    }
    return Object.values(errors).some((value) => Boolean(value));
  });

  const isConfirmDisabled = computed(() => {
    if (!isWordListReady.value) {
      return true;
    }

    if (hasValidationErrors.value) {
      return true;
    }

    const requiredValues = [
      deliveryRequestCreateInput.delivery_address2,
      deliveryRequestCreateInput.delivery_name1,
      deliveryRequestCreateInput.delivery_name2,
      deliveryRequestCreateInput.delivery_name_kana1,
      deliveryRequestCreateInput.delivery_name_kana2,
      deliveryRequestCreateInput.delivery_phone,
      deliveryRequestCreateInput.payment_method,
    ];

    if (requiredValues.some((value) => !value || String(value).trim() === "")) {
      return true;
    }

    if (
      !deliveryRequestCreateInput.area_code ||
      String(deliveryRequestCreateInput.area_code).trim() === ""
    ) {
      return true;
    }

    if (!displayDeliveryTime.value || displayDeliveryTime.value.trim() === "") {
      return true;
    }

    if (!deliveryRequestCreateInput.unit || deliveryRequestCreateInput.unit < 1) {
      return true;
    }

    if (
      paymentRequiredFlg.value &&
      (!dummy_payment_amount.value || dummy_payment_amount.value.trim() === "")
    ) {
      return true;
    }

    if (
      house_card_required.value === 1 &&
      (!deliveryRequestCreateInput.house_card ||
        String(deliveryRequestCreateInput.house_card).trim() === "")
    ) {
      return true;
    }

    return false;
  });

  const doubleDeliCheckLabel = computed(() => {
    return wordList.value.lbl_double_entry_chk.replace(
      "{{n}}",
      (doubleDeliOriconCount.value ?? 0).toString()
    );
  });

  const charterDeliChkLabel = computed(() => {
    return wordList.value.lbl_charter_chk.replace(
      "{{n}}",
      (charterDeliOriconCount.value ?? 0).toString()
    );
  });

  return {
    onClickOpenConfirmDialog,
    hasValidationErrors,
    isConfirmDisabled,
    doubleDeliCheckLabel,
    charterDeliChkLabel,
  };
};
